"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, ArrowRight, Phone } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "item-1",
  question: "What does a kitchen remodel typically cost in Decatur?",
  answer: "Most residential kitchen renovations in Decatur range between $45,000 and $125,000+, depending on square footage, architectural layout changes, and finish selections. Custom solid-wood cabinetry, natural quartz or quartzite countertops, and reconfigured plumbing lines form the core investment. During our initial planning phase, we provide a line-item cost breakdown with clear material allowances so there are no unexpected surprises."
}, {
  id: "item-2",
  question: "How long will the entire design and remodeling process take?",
  answer: "A complete kitchen transformation typically spans 8 to 14 weeks from first architectural concepts to final punch-list sign-off. Detailed design planning and cabinet manufacturing take approximately 4 to 6 weeks upfront. Once demolition begins on-site, active construction runs 6 to 9 weeks, with dedicated project managers overseeing daily trades and scheduled inspections."
}, {
  id: "item-3",
  question: "Do you handle City of Decatur building permits and historic reviews?",
  answer: "Yes. We prepare and submit all architectural drawings, structural engineering calculations, and trade permit filings directly with the City of Decatur Building Division. If your home resides in a designated local historic district (such as Oakhurst or McDonough-Adams-Kings Highway), we manage the Historic Preservation Commission submittals and approvals on your behalf."
}, {
  id: "item-4",
  question: "How do we manage living in our home without a functional kitchen?",
  answer: "We install heavy-duty zip-wall dust containment barriers and negative-air scrubbers to isolate the construction zone from your living quarters. Our team sets up a temporary kitchen station—relocating your refrigerator, a microwave station, and prep surfaces—so daily routines remain manageable throughout active work phases."
}, {
  id: "item-5",
  question: "What return on investment can we expect for Decatur resale value?",
  answer: "Decatur’s strong housing market consistently yields 70% to 82% cost recouped on thoughtfully executed kitchen renovations. Buyers in neighborhoods like Winnona Park and Great Lakes prioritize functional open layouts, high-performance ventilation, durable hardwood or tile flooring, and timeless custom storage solutions that honor classic architecture."
}];
export default function KitchenFaq() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="kitchen-remodeling-faq" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="mx-auto max-w-3xl text-center">
          <div className="flex justify-center mb-3">
            <Badge variant="outline" className="bg-secondary/60 text-foreground border-border uppercase tracking-widest text-xs px-3 py-1 font-semibold">
              <HelpCircle className="size-3.5 text-chart-1 mr-1" />
              Frequent questions
            </Badge>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Decatur kitchen remodeling FAQs
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Straightforward answers about investment ranges, city permitting, realistic construction timelines, and project planning for local homeowners.
          </p>
        </motion.div>

        <div className="mx-auto mt-12 max-w-3xl">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqItems.map((faq, index) => <motion.div key={faq.id} data-index={index} variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }}>
                <AccordionItem value={faq.id} className="rounded-lg bg-card border border-border px-6 py-1 shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-chart-1/40 hover:shadow-md">
                  <AccordionTrigger className="text-left font-sans text-base sm:text-lg font-semibold text-card-foreground hover:no-underline py-5 cursor-pointer">
                    <span className="pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed pt-1 pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>)}
          </Accordion>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="mx-auto mt-12 max-w-3xl rounded-xl bg-secondary/50 border border-border p-6 sm:p-8 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="font-serif text-lg sm:text-xl font-semibold text-foreground">
              Have specific questions about your home?
            </h3>
            <p className="text-sm text-muted-foreground font-sans">
              Speak directly with our Decatur design and construction team to review layout ideas and budget guidelines.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
            <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
              <a href="tel:+14708479076" className="flex items-center gap-2">
                <Phone className="size-4 text-chart-1" />
                <span>(470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="default" size="sm" className="w-full sm:w-auto bg-chart-1 text-primary-foreground hover:bg-chart-1/90">
              <Link to="#" className="flex items-center gap-2">
                <span>Book consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}